import { useState, useRef } from "react";
import {
  format,
  isSameDay,
  areIntervalsOverlapping,
  addMinutes,
} from "date-fns";
import { useCalendar } from "../calendar-context";
import { cn } from "@/lib/utils";
import { Lock } from "lucide-react";
import type { Event, TimeRange, SlotStatus } from "@/lib/calendar-types";
import { generateHourlyTimeSlots } from "@/lib/calendar-utils";

export default function DailyView() {
  const {
    currentDate,
    events,
    selectedRanges,
    bookedRanges,
    onAddTimeRange,
    onEditEvent,
    showConflictToast,
    onSlotClick,
    selectionMode,
    enforceRangeRestriction,
  } = useCalendar();

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Date | null>(null);
  const [dragEnd, setDragEnd] = useState<Date | null>(null);
  const [previewSlots, setPreviewSlots] = useState<Date[]>([]);
  const [hasConflict, setHasConflict] = useState(false);
  const [initialSlotSelected, setInitialSlotSelected] = useState<
    boolean | null
  >(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const timeSlots = generateHourlyTimeSlots(currentDate);

  // Define constants for time slot rendering
  const hourHeight = 80; // Height of each hour slot in pixels
  const slotsPerHour = 2; // Number of slots per hour (30 min slots)

  const handleMouseDown = (slot: Date) => {
    // If the slot is booked or outside available segments, ignore the click
    if (isSlotBooked(slot) || isSlotOutsideUserDefinedRanges(slot)) {
      return;
    }

    // If onSlotClick is provided, call it and return
    if (onSlotClick) {
      onSlotClick(slot);
      return;
    }

    // Determine if the initial slot is selected
    const isSelected = isSlotInSelectedRange(slot);
    setInitialSlotSelected(isSelected);

    // Start dragging
    setIsDragging(true);
    setDragStart(slot);
    setDragEnd(slot);
    setPreviewSlots([slot]);
    setHasConflict(false);
  };

  const handleMouseMove = (slot: Date) => {
    if (!isDragging || !dragStart || initialSlotSelected === null) return;

    setDragEnd(slot);

    // Calculate all slots between dragStart and current slot
    const startTime = dragStart.getTime();
    const currentTime = slot.getTime();
    const start = Math.min(startTime, currentTime);
    const end = Math.max(startTime, currentTime);

    const newPreviewSlots = timeSlots.filter(
      (timeSlot) => timeSlot.getTime() >= start && timeSlot.getTime() <= end,
    );

    // If we're deselecting (initial slot was selected), filter out booked slots
    if (initialSlotSelected) {
      const deselectionSlots = newPreviewSlots.filter(
        (slot) => !isSlotBooked(slot),
      );
      setPreviewSlots(deselectionSlots);
      setHasConflict(false);
    } else {
      // If we're selecting, check for conflicts with booked slots
      const conflict = newPreviewSlots.some((slot) => isSlotBooked(slot));
      setHasConflict(conflict);
      setPreviewSlots(newPreviewSlots);
    }
  };

  const handleMouseUp = () => {
    if (
      isDragging &&
      previewSlots.length > 0 &&
      !hasConflict &&
      initialSlotSelected !== null
    ) {
      // Sort the preview slots by time
      const sortedSlots = [...previewSlots].sort(
        (a, b) => a.getTime() - b.getTime(),
      );

      // Create a time range from the first and last slots
      const timeRange: TimeRange = {
        id: crypto.randomUUID(),
        startDate: sortedSlots[0],
        endDate: addMinutes(sortedSlots[sortedSlots.length - 1], 30), // Add 30 minutes to include the full slot
        dayName: format(sortedSlots[0], "EEEE"),
      };

      // Use the initial slot status to determine the action
      if (initialSlotSelected) {
        // If initial slot was selected, we're deselecting
        onAddTimeRange(timeRange);
      } else {
        // If initial slot was vacant, we're selecting
        onAddTimeRange(timeRange);
      }
    } else if (hasConflict) {
      showConflictToast(
        "Cannot select this range as it overlaps with booked time slots.",
      );
    }

    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
    setPreviewSlots([]);
    setHasConflict(false);
    setInitialSlotSelected(null);
  };

  const getDayEvents = () => {
    return events.filter((event) => {
      const eventStart = new Date(event.start);
      return isSameDay(eventStart, currentDate);
    });
  };

  const getEventPosition = (event: Event) => {
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);

    // Calculate top position (minutes from start of day / total minutes in day * 100%)
    const dayStart = new Date(currentDate);
    dayStart.setHours(0, 0, 0, 0);

    const minutesFromDayStart =
      (eventStart.getTime() - dayStart.getTime()) / (1000 * 60);
    const top = (minutesFromDayStart / (24 * 60)) * 100;

    // Calculate height (duration in minutes / total minutes in day * 100%)
    const durationMinutes =
      (eventEnd.getTime() - eventStart.getTime()) / (1000 * 60);
    const height = (durationMinutes / (24 * 60)) * 100;

    return { top, height };
  };

  const isSlotInPreview = (slot: Date) => {
    return previewSlots.some(
      (previewSlot) => previewSlot.getTime() === slot.getTime(),
    );
  };

  const isSlotInSelectedRange = (slot: Date) => {
    const slotTime = slot.getTime();
    if (!selectedRanges || selectedRanges.length === 0) return false;

    return selectedRanges.some((range) => {
      // Check if range.startDate and range.endDate are Date objects
      if (
        !(range.startDate instanceof Date) ||
        !(range.endDate instanceof Date)
      ) {
        // If they're strings or timestamps, try to convert them to Date objects
        const startDate =
          range.startDate instanceof Date
            ? range.startDate
            : new Date(range.startDate);
        const endDate =
          range.endDate instanceof Date
            ? range.endDate
            : new Date(range.endDate);

        return (
          !isNaN(startDate.getTime()) &&
          !isNaN(endDate.getTime()) &&
          slotTime >= startDate.getTime() &&
          slotTime < endDate.getTime()
        );
      }

      return (
        slotTime >= range.startDate.getTime() &&
        slotTime < range.endDate.getTime()
      );
    });
  };

  const isSlotBooked = (slot: Date) => {
    const slotTime = slot.getTime();
    const slotDay = format(slot, "EEEE");

    return (
      bookedRanges?.some(
        (range) =>
          range.dayName === slotDay &&
          slotTime >= range.startDate.getTime() &&
          slotTime < range.endDate.getTime(),
      ) || false
    );
  };

  // Function to check if a slot is outside available segments
  const isSlotOutsideUserDefinedRanges = (slot: Date) => {
    // If we're not enforcing range restrictions or there are no ranges, all slots are available
    if (
      !enforceRangeRestriction ||
      !selectedRanges ||
      selectedRanges.length === 0
    )
      return false;

    const slotTime = slot.getTime();
    const slotDay = format(slot, "EEEE");

    // Check if the slot falls within any of the user-defined ranges
    const isWithinAnyRange = selectedRanges.some((range) => {
      // Ensure dates are Date objects
      const startDate =
        range.startDate instanceof Date
          ? range.startDate
          : new Date(range.startDate);
      const endDate =
        range.endDate instanceof Date ? range.endDate : new Date(range.endDate);

      // Check if the slot is within this range and on the same day
      return (
        range.dayName === slotDay &&
        slotTime >= startDate.getTime() &&
        slotTime < endDate.getTime()
      );
    });

    // Return true if the slot is outside all defined ranges and we are enforcing restrictions
    return !isWithinAnyRange;
  };

  const getSlotStatus = (slot: Date): SlotStatus => {
    // First check if the slot is booked - highest priority
    if (isSlotBooked(slot)) return "booked";

    // Check if the slot is outside available segments
    if (isSlotOutsideUserDefinedRanges(slot)) return "outside-range";

    // Then check if it's in the preview or selected
    if (isSlotInPreview(slot)) {
      return hasConflict ? "conflict" : "selected";
    }

    if (isSlotInSelectedRange(slot)) return "selected";

    // If none of the above, it's available
    return "available";
  };

  const isSlotOccupied = (slot: Date) => {
    const slotStart = new Date(slot);
    const slotEnd = addMinutes(slot, 30);

    return events.some((event) => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);

      return areIntervalsOverlapping(
        { start: slotStart, end: slotEnd },
        { start: eventStart, end: eventEnd },
      );
    });
  };

  return (
    <div
      className="daily-view h-[600px] overflow-y-auto relative"
      ref={containerRef}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="time-slots-container relative">
        {/* Time labels */}
        <div className="time-labels absolute left-0 top-0 w-16 h-full border-r border-border">
          {Array.from({ length: 24 }).map((_, i) => (
            <div
              key={i}
              className="time-label h-20 flex items-start justify-center text-sm text-muted-foreground pt-1"
            >
              {i === 0
                ? "12 AM"
                : i < 12
                  ? `${i} AM`
                  : i === 12
                    ? "12 PM"
                    : `${i - 12} PM`}
            </div>
          ))}
        </div>

        {/* Time slots */}
        <div className="time-slots ml-16 relative">
          {/* Hour lines */}
          {Array.from({ length: 24 }).map((_, i) => (
            <div
              key={i}
              className="hour-line border-t border-border flex flex-col"
              style={{ height: `${hourHeight}px` }}
            >
              {/* Generate slots based on slotDuration */}
              {Array.from({ length: slotsPerHour }).map((_, slotIndex) => {
                const timeSlotIndex = i * slotsPerHour + slotIndex;
                const timeSlot = timeSlots[timeSlotIndex] || timeSlots[i * 2]; // Fallback to existing slots if needed

                return (
                  <div
                    key={slotIndex}
                    className={cn(
                      "livekit-time-slot border-dashed border-t border-border relative",
                      getSlotStatus(timeSlot) === "booked" &&
                        "livekit-time-slot-booked",
                      getSlotStatus(timeSlot) === "selected" &&
                        "livekit-time-slot-selected",
                      getSlotStatus(timeSlot) === "conflict" &&
                        "livekit-time-slot-conflict",
                      getSlotStatus(timeSlot) === "outside-range" &&
                        "bg-muted/50 cursor-not-allowed opacity-50 pointer-events-none",
                      getSlotStatus(timeSlot) === "available" &&
                        "livekit-time-slot-available",
                    )}
                    style={{ height: `${hourHeight / slotsPerHour}px` }}
                    onMouseDown={() => {
                      // Only allow clicks on available segments
                      if (
                        !isSlotBooked(timeSlot) &&
                        !isSlotOutsideUserDefinedRanges(timeSlot)
                      ) {
                        handleMouseDown(timeSlot);
                      }
                    }}
                    onMouseMove={() => {
                      if (isDragging && selectionMode !== "click-only") {
                        handleMouseMove(timeSlot);
                      }
                    }}
                  >
                    {getSlotStatus(timeSlot) === "booked" && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Lock className="h-3 w-3 text-danger" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {/* Current time indicator */}
          <div
            className="current-time-indicator absolute left-0 w-full border-t border-danger-line z-10"
            style={{
              top: `${((new Date().getHours() * 60 + new Date().getMinutes()) / (24 * 60)) * 100}%`,
            }}
          >
            <div className="w-2 h-2 rounded-full bg-destructive -mt-1 -ml-1"></div>
          </div>

          {/* Events */}
          {getDayEvents().map((event) => {
            const { top, height } = getEventPosition(event);
            return (
              <div
                key={event.id}
                className="absolute left-1 right-1 rounded-lg p-2 text-white text-sm overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                style={{
                  top: `${top}%`,
                  height: `${height}%`,
                  backgroundColor: event.color || "#5046e5",
                }}
                onClick={() => onEditEvent(event)}
              >
                <div className="font-semibold">{event.title}</div>
                <div className="text-xs">
                  {format(new Date(event.start), "h:mm a")} -{" "}
                  {format(new Date(event.end), "h:mm a")}
                </div>
              </div>
            );
          })}

          {/* Booked time ranges */}
          {bookedRanges
            .filter(
              (range) =>
                format(range.startDate, "yyyy-MM-dd") ===
                format(currentDate, "yyyy-MM-dd"),
            )
            .map((bookedRange) => {
              const dayStart = new Date(currentDate);
              dayStart.setHours(0, 0, 0, 0);

              const minutesFromDayStart =
                (bookedRange.startDate.getTime() - dayStart.getTime()) /
                (1000 * 60);
              const top = (minutesFromDayStart / (24 * 60)) * 100;

              const durationMinutes =
                (bookedRange.endDate.getTime() -
                  bookedRange.startDate.getTime()) /
                (1000 * 60);
              const height = (durationMinutes / (24 * 60)) * 100;

              return (
                <div
                  key={bookedRange.id}
                  className="absolute left-1 right-1 rounded-lg p-2 text-white text-sm overflow-hidden pointer-events-none z-20 bg-destructive bg-opacity-80 shadow-sm"
                  style={{
                    top: `${top}%`,
                    height: `${height}%`,
                  }}
                >
                  <div className="flex items-center">
                    <Lock className="h-3 w-3 mr-1" />
                    <div className="font-semibold">{bookedRange.title}</div>
                  </div>
                  <div className="text-xs">
                    {format(bookedRange.startDate, "h:mm a")} -{" "}
                    {format(bookedRange.endDate, "h:mm a")}
                  </div>
                  {bookedRange.bookedBy && (
                    <div className="text-xs mt-1">
                      Booked by: {bookedRange.bookedBy}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
